import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  getVisibleElements,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const { scroller } = this.refs;
    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    // Infinite loop: some engines (notably WebKit/Safari) never synthesize
    // mousedown/pointerdown for a native touch pan on a scrollable element, so
    // #handleMouseDown's drag-wrap fix above never runs for those gestures —
    // the carousel is left relying purely on native scrolling, which has no
    // boundary-wrap logic of its own. Detect an overscroll gesture pinned at
    // the scroller's edge (wheel or touch) and wrap via the existing
    // next()/previous(), mirroring the same pattern already shipped in
    // video-reels.js.
    scroller.addEventListener('wheel', this.#handleWheel, { passive: true });
    scroller.addEventListener('touchstart', this.#handleTouchStart, { passive: true });
    scroller.addEventListener('touchend', this.#handleTouchEnd, { passive: true });

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      let visibleSlidesAmount = 0;
      const initialSlideId = this.initialSlide?.getAttribute('slide-id');
      if (this.initialSlideIndex !== 0 && initialSlideId) {
        this.select({ id: initialSlideId }, undefined, { animate: false });
        visibleSlidesAmount = 1;
      } else {
        visibleSlidesAmount = this.#updateVisibleSlides();
        if (visibleSlidesAmount === 0) {
          this.select(0, undefined, { animate: false });
          visibleSlidesAmount = 1;
        }
      }

      // The geometry read above is meaningless if this container currently has zero size (e.g.
      // it's inside a `display: none` tab panel that hasn't been activated yet) - in that case
      // `visibleSlidesAmount` was force-defaulted above and can't be trusted to gate whether the
      // resize observer below is allowed to ever recompute again.
      const { width, height } = this.refs.slideshowContainer.getBoundingClientRect();
      let needsRemeasure = width === 0 && height === 0;

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;

        if (needsRemeasure || visibleSlidesAmount > 1) {
          visibleSlidesAmount = this.#updateVisibleSlides();
          needsRemeasure = false;
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const { scroller } = this.refs;

    scroller.removeEventListener('mousedown', this.#handleMouseDown);
    scroller.removeEventListener('wheel', this.#handleWheel);
    scroller.removeEventListener('touchstart', this.#handleTouchStart);
    scroller.removeEventListener('touchend', this.#handleTouchEnd);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#scroll?.destroy();

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        return this.slides.indexOf(requestedSlide);
      }
    })();

    const { current } = this;

    // Guard if invalid
    if (requestedIndex === undefined || isNaN(requestedIndex) || requestedIndex === current) return;

    const { slides } = this;

    if (!slides?.length) return;
    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;

    // If jump is more than 1 or we looped, do the placeholder + reorder trick
    if (!instant && !isAdjacentSlide && visibleSlides.length === 1) {
      this.#disabled = true;
      await this.#scroll.finished; // ensure we're not mid-scroll

      const targetSlide = slides[index];
      const currentSlide = slides[current];
      if (!targetSlide || !currentSlide) return;

      // Create a placeholder in the original DOM position of targetSlide
      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      // Decide whether targetSlide goes before or after currentSlide
      // so that we scroll a short distance in the correct direction
      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      // Once that scroll finishes, restore the DOM
      queueMicrotask(async () => {
        await this.#scroll.finished;
        this.#disabled = false;

        // Restore the slide back to its original position. This triggers a scroll event.
        placeholder.replaceWith(targetSlide);

        // Instantly scroll to the target slide as its position will have changed
        this.#scroll.to(targetSlide, { instant: true });
      });
    }

    const slide = slides[index];
    if (!slide) return;

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');
    this.#scroll.to(slide, { instant });
    this.current = this.slides?.indexOf(slide) || 0;

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {Object} [options] - The options for the next slide.
   * @param {boolean} [options.animate=true] - Whether to animate the next slide.
   */
  next(event, options) {
    event?.preventDefault();
    if (this.singleStep) {
      this.#step(1, options);
      return;
    }
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {Object} [options] - The options for the previous slide.
   * @param {boolean} [options.animate=true] - Whether to animate the previous slide.
   */
  previous(event, options) {
    event?.preventDefault();
    if (this.singleStep) {
      this.#step(-1, options);
      return;
    }
    this.select(this.previousIndex, event, options);
  }

  /**
   * Steps to the slide immediately before/after whichever slide is
   * currently anchoring the leading edge of the viewport, in the given
   * direction.
   *
   * Unlike select(), this never trusts a cached index or a hand-measured
   * pixel step. It re-derives the anchor from `visibleSlides` (whichever
   * slide is actually leftmost in view, right now) on every call, then
   * scrolls to that anchor's immediate DOM neighbour using the same
   * element-alignment Scroller.to() already uses elsewhere in this file —
   * so the browser computes the exact offset itself instead of us
   * estimating a slide-width-plus-gap delta via scrollBy(). That estimate
   * is what caused the asymmetric overshoot (one arrow fighting the
   * track's CSS scroll-snap, the other not): the fix is to stop guessing
   * the distance and just ask to align with a specific, real element.
   * @param {1 | -1} direction
   * @param {Object} [options]
   * @param {boolean} [options.animate=true]
   */
  #step(direction, options = {}) {
    // One-shot, synchronous only — never wired to a scroll/timer event, so
    // it can't re-trigger itself or fight an in-flight animation. See
    // #recycleOneSlide() for what it does and why.
    if (this.seamlessLoop) this.#recycleOneSlide(direction);

    const { slides, visibleSlides } = this;
    if (!slides?.length) return;

    const { animate = true } = options;
    const instant = prefersReducedMotion() || !animate;

    const anchor = visibleSlides[0] ?? slides[this.current] ?? slides[0];
    const anchorIndex = anchor ? slides.indexOf(anchor) : -1;
    let targetIndex = (anchorIndex < 0 ? this.current : anchorIndex) + direction;

    const lastIndex = slides.length - 1;

    // When several slides are visible at once, the anchor (first visible slide)
    // plateaus once the scroller physically can't scroll any further in this
    // direction — targetIndex then never exceeds the bounds check below, so an
    // infinite carousel silently stalls at the end instead of looping. Detect
    // that we've hit the real scroll boundary and treat it the same as being
    // out of bounds so the wraparound below still kicks in.
    const { scroller } = this.refs;
    const { axis } = this.#scroll;
    const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const scrollSizeProp = axis === 'x' ? 'scrollWidth' : 'scrollHeight';
    const clientSizeProp = axis === 'x' ? 'clientWidth' : 'clientHeight';
    const maxScroll = scroller[scrollSizeProp] - scroller[clientSizeProp];
    const atScrollEnd = direction > 0 && scroller[scrollProp] >= maxScroll - 1;
    const atScrollStart = direction < 0 && scroller[scrollProp] <= 1;

    if (atScrollEnd || atScrollStart || targetIndex < 0 || targetIndex > lastIndex) {
      if (!this.infinite) return;
      targetIndex = direction > 0 ? 0 : lastIndex;
    }

    const target = slides[targetIndex];
    if (!target) return;

    this.#scroll.to(target, { instant });
    this.current = targetIndex;
  }

  /**
   * If the slide at the leading edge (relative to `direction`) is already
   * fully scrolled out of view, permanently move just that one slide to the
   * opposite end and instantly compensate the scroll position so nothing
   * currently visible shifts — before this click's own scroll starts. It's
   * never moved back afterward, so there's nothing here that can produce a
   * visible reset later. Runs exactly once, synchronously, only from
   * #step() in direct response to a click — deliberately not attached to
   * any scroll/timer event, so it can never re-enter itself or fight an
   * in-flight scroll animation.
   * @param {1 | -1} direction
   */
  #recycleOneSlide(direction) {
    if (!this.infinite) return;

    const { scroller } = this.refs;
    const { axis } = this.#scroll;
    const startProp = axis === 'x' ? 'offsetLeft' : 'offsetTop';
    const sizeProp = axis === 'x' ? 'offsetWidth' : 'offsetHeight';
    const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const clientSizeProp = axis === 'x' ? 'clientWidth' : 'clientHeight';
    const scrollToKey = axis === 'x' ? 'left' : 'top';

    const slides = this.slides;
    if (!slides || slides.length < 3) return;

    const viewStart = scroller[scrollProp];
    const viewEnd = viewStart + scroller[clientSizeProp];

    if (direction > 0) {
      const first = slides[0];
      const second = slides[1];
      if (!first || !second) return;
      if (first[startProp] + first[sizeProp] > viewStart) return; // still (partly) visible — leave it

      const gap = second[startProp] - first[startProp] - first[sizeProp];
      const removed = first[sizeProp] + gap;
      scroller.appendChild(first);
      // scroller has CSS scroll-behavior: smooth, which also governs plain
      // scrollLeft assignments — behavior: 'instant' forces a real jump.
      scroller.scrollTo({ [scrollToKey]: viewStart - removed, behavior: 'instant' });
    } else {
      const last = slides[slides.length - 1];
      const secondLast = slides[slides.length - 2];
      if (!last || !secondLast) return;
      if (last[startProp] < viewEnd) return; // still (partly) visible — leave it

      const gap = last[startProp] - secondLast[startProp] - secondLast[sizeProp];
      const removed = last[sizeProp] + gap;
      scroller.prepend(last);
      scroller.scrollTo({ [scrollToKey]: viewStart + removed, behavior: 'instant' });
    }
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    if (current) current.textContent = `${value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `${i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return getVisibleElements(this.refs.scroller, this.slides, SLIDE_VISIBLITY_THRESHOLD, 'x');
  }

  get previousIndex() {
    const { current, visibleSlides, singleStep } = this;

    // Note: single-step navigation itself happens in #step(), not here —
    // this getter now only feeds the previous-button disabled check below
    // for non-infinite carousels, so it just needs "one slide back".
    if (singleStep) return current - 1;

    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current - modifier;
  }

  get nextIndex() {
    const { current, visibleSlides, singleStep } = this;

    // See previousIndex: only used for the next-button disabled check.
    if (singleStep) return current + 1;

    const modifier = visibleSlides.length > 1 ? visibleSlides.length : 1;
    return current + modifier;
  }

  /**
   * Whether arrow clicks should always advance by exactly one slide,
   * regardless of how many slides are visible at once (e.g. product card
   * carousels showing several cards per view). Opt in with the
   * `single-step` attribute.
   * @type {boolean}
   */
  get singleStep() {
    return this.hasAttribute('single-step');
  }

  /**
   * Whether wrapping past the first/last slide (single-step + infinite)
   * should recycle real slide elements to the opposite end instead of
   * snapping/rewinding to the real first/last slide's physical position.
   * Opt in with the `seamless-loop` attribute.
   * @type {boolean}
   */
  get seamlessLoop() {
    return this.hasAttribute('seamless-loop');
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    const visibleSlides = this.visibleSlides;

    if (!visibleSlides.length) return this.current;

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const centers = visibleSlides.map((slide) => center(slide, axis));
    const referencePoint = visibleSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = visibleSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /** @type {boolean} Guards against re-firing a boundary bounce from one continuous overscroll gesture */
  #boundaryBounceLocked = false;

  /** @type {number} Accumulated wheel delta while pinned at a scroll boundary */
  #wheelOverscroll = 0;

  /** @type {number|undefined} */
  #wheelResetTimer;

  /** @type {number} Touch X (or Y, for a vertical slideshow) at touchstart */
  #touchStartPos = 0;

  /** @type {1 | -1 | 0} Which edge the scroller was pinned at on touchstart; 0 = not pinned */
  #touchPinnedEdge = 0;

  /**
   * Reads the scroller's current position/extent along the active axis.
   * @returns {{ position: number, max: number }}
   */
  #getScrollBoundaryState() {
    const { scroller } = this.refs;
    const { axis } = this.#scroll;
    const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';
    const scrollSizeProp = axis === 'x' ? 'scrollWidth' : 'scrollHeight';
    const clientSizeProp = axis === 'x' ? 'clientWidth' : 'clientHeight';
    return {
      position: scroller[scrollProp],
      max: scroller[scrollSizeProp] - scroller[clientSizeProp],
    };
  }

  /**
   * Wraps to the opposite end in response to a wheel/touch gesture that
   * overscrolls past a pinned scroll boundary. Locks briefly so trailing
   * events from the same gesture can't double-trigger.
   * @param {1 | -1} direction
   */
  #triggerBoundaryBounce(direction) {
    this.#boundaryBounceLocked = true;
    if (direction > 0) this.next();
    else this.previous();
    setTimeout(() => {
      this.#boundaryBounceLocked = false;
    }, 700);
  }

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    if (!this.infinite || this.#boundaryBounceLocked || this.#dragging) return;

    const { axis } = this.#scroll;
    const delta = axis === 'x' ? (event.deltaX !== 0 ? event.deltaX : event.deltaY) : event.deltaY;
    if (delta === 0) return;

    const { position, max } = this.#getScrollBoundaryState();
    const atEnd = position >= max - 1;
    const atStart = position <= 1;

    if ((delta > 0 && atEnd) || (delta < 0 && atStart)) {
      this.#wheelOverscroll += delta;
      clearTimeout(this.#wheelResetTimer);
      this.#wheelResetTimer = window.setTimeout(() => {
        this.#wheelOverscroll = 0;
      }, 250);

      const WHEEL_THRESHOLD = 60;
      if (Math.abs(this.#wheelOverscroll) > WHEEL_THRESHOLD) {
        const direction = this.#wheelOverscroll > 0 ? 1 : -1;
        this.#wheelOverscroll = 0;
        this.#triggerBoundaryBounce(direction);
      }
    } else {
      this.#wheelOverscroll = 0;
    }
  };

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    if (!this.infinite) return;
    const { axis } = this.#scroll;
    const { position, max } = this.#getScrollBoundaryState();
    this.#touchPinnedEdge = position >= max - 1 ? 1 : position <= 1 ? -1 : 0;
    const touch = event.touches[0];
    this.#touchStartPos = axis === 'x' ? touch.clientX : touch.clientY;
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    if (!this.infinite || !this.#touchPinnedEdge || this.#boundaryBounceLocked || this.#dragging) {
      this.#touchPinnedEdge = 0;
      return;
    }

    const pinnedEdge = this.#touchPinnedEdge;
    this.#touchPinnedEdge = 0;

    const { axis } = this.#scroll;
    const { position, max } = this.#getScrollBoundaryState();
    const touch = event.changedTouches[0];
    const endPos = axis === 'x' ? touch.clientX : touch.clientY;
    const delta = this.#touchStartPos - endPos; // positive = swiped toward "next"
    const stillPinnedSameEdge = (pinnedEdge === 1 && position >= max - 1) || (pinnedEdge === -1 && position <= 1);

    const TOUCH_SWIPE_THRESHOLD = 30;
    if (stillPinnedSameEdge && Math.abs(delta) > TOUCH_SWIPE_THRESHOLD) {
      if (pinnedEdge === 1 && delta > 0) this.#triggerBoundaryBounce(1);
      else if (pinnedEdge === -1 && delta < 0) this.#triggerBoundaryBounce(-1);
    }
  };

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const rawIndex = next + modifier;
      const lastIndex = slides.length - 1;

      // When multiple slides are visible at once, #sync() can settle on the same
      // index near either end even after a real flick (the same ambiguity #step()
      // has to guard against), so rawIndex may never actually land outside the
      // array bounds. Detect that the flick happened while already pinned at the
      // real scroll boundary and treat that as reaching the edge too, so infinite
      // carousels wrap instead of silently absorbing the drag.
      const { axis } = this.#scroll;
      const scrollProp = axis === 'x' ? 'scrollLeft' : 'scrollTop';
      const scrollSizeProp = axis === 'x' ? 'scrollWidth' : 'scrollHeight';
      const clientSizeProp = axis === 'x' ? 'clientWidth' : 'clientHeight';
      const maxScroll = scroller[scrollSizeProp] - scroller[clientSizeProp];
      const realFlick = direction !== 0 && Math.abs(velocity) >= 10 && distanceTravelled >= 10;
      const atScrollEnd = realFlick && direction > 0 && scroller[scrollProp] >= maxScroll - 1;
      const atScrollStart = realFlick && direction < 0 && scroller[scrollProp] <= 1;

      // Wrap instead of clamping when the carousel is infinite, so a drag/swipe
      // past the last (or before the first) slide loops around — matching the
      // wraparound select() and #step() already provide for programmatic
      // navigation. Non-infinite carousels keep the exact same clamp() result.
      let newIndex;
      if (this.infinite && (atScrollEnd || atScrollStart || rawIndex < 0 || rawIndex > lastIndex)) {
        newIndex = direction > 0 ? 0 : lastIndex;
      } else {
        newIndex = clamp(rawIndex, 0, lastIndex);
      }

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index ${newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `${!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
